import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Alert, Container, Form, Row, Button, Col, Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router';
import { Applications } from '../../api/Applications';
import BdaApplicationItem from '../../components/list-items/BdaApplicationItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import FileSaver from 'file-saver';
import AppSearch from '../../components/common/AppSearch';
import { Select, Pagination, Checkbox } from 'antd';
import { useAuth } from '../../contexts/auth';
import { TailSpin } from 'react-loader-spinner';
import { accessLevelOptions } from '../../utils';
import { useMemo } from 'react';
import { cloneDeep, debounce } from 'lodash';
import { EventEmitter } from '../../utils/EventEmitter';
import { useCallback } from 'react';
import { BdaApplication } from '../../models/BdaApplication';
import { PagedResults } from '../../models/generics';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

const modesOptions = [
  { label: 'Batch', value: 'batch', icon: solid('cog') },
  { label: 'Stream', value: 'stream', icon: solid('stream') },
];

const statusOptions = [
  { label: "Ready", value: "READY" },
  { label: "Starting", value: "STARTING" },
  { label: "Running", value: "RUNNING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Stopped", value: "STOPPED" },
  { label: "Error", value: "ERROR" },
];

export default function BdaApplicationsList() {
  const [applications, setApplications] = useState<BdaApplication[]>();
  const [error, setError] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [modeFilter, setModeFilter] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [accessLevelFilter, setAccessLevelFilter] = useState<string>();
  const [sortDesc, setSortDesc] = useState(true);
  const [myApplications, setMyApplications] = useState(false);
  const [defaultOnly, setDefaultOnly] = useState(true);
  const [activeSchedules, setActiveSchedules] = useState(false);

  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalApplications, setTotalApplications] = useState<number>();
  const [currentPage, setCurrentPage] = useState(1);

  const [applicationToImport, setApplicationToImport] = useState<string>();
  const [applicationImportError, setApplicationImportError] = useState<string>();
  const [applicationToDelete, setApplicationToDelete] = useState<BdaApplication>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleDeleteModalClose = () => { setShowDeleteModal(false); setApplicationToDelete(undefined) };
  const handleImportModalClose = () => setShowImportModal(false);
  const openImportModal = () => setShowImportModal(true);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const { loggedUser } = useAuth();
  const navigate = useNavigate();

  const handleStatusMessageEvent = useCallback((evt: any) => {
    const statusMessage = evt.details;
    console.log('received message', statusMessage, applications);
    if (statusMessage.bda && !statusMessage.serviceId) {
      const index = applications!.findIndex(app => app.id === statusMessage.bda.id);
      if (index > -1) {
        const newApplications = cloneDeep(applications!);
        newApplications[index].status = statusMessage.bda.status;
        setApplications(newApplications);
      }
    }
  }, [applications]);

  useEffect(() => {
    if (applications) EventEmitter.subscribe('BDA_APP_STATUS_CHANGE', handleStatusMessageEvent)

    return () => EventEmitter.unsubscribe('BDA_APP_STATUS_CHANGE');
  }, [handleStatusMessageEvent, applications]);

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [currentPage]);

  // handling current page higher than actual available pages edge case
  useEffect(() => {
    if (currentPage > 1) setCurrentPage(1);
    else reset();
    // eslint-disable-next-line
  }, [itemsPerPage, sortDesc, myApplications, activeSchedules, defaultOnly, searchText, modeFilter, statusFilter, accessLevelFilter]);

  const getApplications = () => {
    const params = new URLSearchParams();
    params.set("page", `${currentPage}`);
    params.set("size", `${itemsPerPage}`);
    params.set("sort", "id");
    params.set("desc", `${sortDesc}`);
    if (statusFilter?.length) params.set("status", statusFilter.join(","));
    if (modeFilter) params.set("mode", modeFilter);
    // params.set("name", searchText);
    if (myApplications) params.set("createdBy", loggedUser!.userId!);
    if (defaultOnly) params.set("defaultVersion", `${defaultOnly}`);
    if (accessLevelFilter) params.set("accessLevel", accessLevelFilter);
    if (searchText.length) params.set("freeText", searchText);
    if (activeSchedules) params.set("activeSchedule", `${activeSchedules}`);
    // params.set("tags", sortDesc);

    Applications.getApplications(params)
      .then(
        ({ data: appPages }) => {
          setApplications((appPages as PagedResults<BdaApplication>).content);
          setTotalApplications((appPages as PagedResults<BdaApplication>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('apps-error', 'Could not get applications', 'Applications Error', 'danger');
          setApplications([]);
          setError(true);
        }
      );
  }

  const navigateToCreation = () => navigate('/bda-applications/create');
  const navigateToDetail = (app: BdaApplication) => () => navigate(`/bda-applications/${app.id}`);
  const changeSortMode = () => setSortDesc(!sortDesc);
  const handleMyApplicationsChange = (evt: CheckboxChangeEvent) => setMyApplications(evt.target.checked);
  const handleDefaultOnlyChange = (evt: CheckboxChangeEvent) => setDefaultOnly(evt.target.checked);
  const handleSchedulesChange = (evt: CheckboxChangeEvent) => setActiveSchedules(evt.target.checked);

  const navigateToEdit = (app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/bda-applications/${app.id}/edit`);
  };

  const reset = () => {
    setError(false);
    setApplications(undefined);
    getApplications();
  }

  const downloadApplication = (app: BdaApplication) => async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    showLoader();

    try {
      const { data: application } = await Applications.getApplication(app.id!);

      const appString = JSON.stringify(application, null, 2);
      const fileContent = new Blob([appString], { type: 'text/json' });
      FileSaver.saveAs(fileContent, `Alida BDA Application - ${app.name}.json`);
      hideLoader();
    } catch (e) {
      console.log(e);
      showToast('bda-export-error', 'Could not export application.', 'Application Error', 'danger');
      hideLoader();
    }

  }

  const handleApplicationFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(evt.target.files!)[0];
    const reader = new FileReader();
    reader.readAsText(file, "UTF-8");
    reader.onload = function (e) {
      try {
        const applicationDefinition = e.target!.result as string;
        setApplicationToImport(applicationDefinition);
      }
      catch (e) {
        console.log(e);
        setApplicationImportError('Application definition file must be a valid JSON.');
      }
    }
  }

  const importApplication = () => {
    if (!applicationToImport) {
      setApplicationImportError('You must select a file for the application to import first.');
      return;
    }
    setApplicationImportError(undefined);
    localStorage.setItem('applicationToImport', applicationToImport);
    setShowImportModal(false);
    navigate('/bda-applications/create?shouldImportApp=true');
  }

  const openDeleteModal = (app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setApplicationToDelete(app);
  }

  const deleteApplication = () => {
    showLoader();

    Applications.deleteApplication(applicationToDelete!.id!)
      .then(
        () => {
          showToast('bda-delete-success', 'Application deleted successfully.', 'Success', 'success');
          hideLoader();
          handleDeleteModalClose();
          reset();
        },
        () => {
          showToast('bda-delete-error', 'Could not delete application.', 'Application Error', 'danger');
          hideLoader();
        }
      );
  }

  const toggleApplication = (app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    ["RUNNING", "PARTIAL", "STARTING"].includes(app.status as string) ? stopApplication(app) : runApplication(app);
  }

  const runApplication = async (app: BdaApplication) => {
    showLoader();

    try {
      const { data: application } = await Applications.getApplication(app.id!);
      const workflowId = application.workflows[0].id!;

      Applications.runApplication(app.id!, workflowId)
        .then(
          (_) => {
            showToast('bda-run-success', 'Application started successfully.', 'Success', 'success');
            hideLoader();
          },
          (err) => {
            showToast('bda-run-error', 'Could not execute application.', 'Application Error', 'danger');
            hideLoader();
          }
        );
    } catch (e) {
      console.log(e);
      showToast('bda-run-error', 'Could not execute application.', 'Application Error', 'danger');
      hideLoader();
    }
  }

  const stopApplication = async (app: BdaApplication) => {
    showLoader();

    try {
      const { data: application } = await Applications.getApplication(app.id!);
      const workflowId = application.workflows[0].id!;

      Applications.stopApplication(app.id!, workflowId)
        .then(
          (_) => {
            showToast('bda-stop-success', 'Application stopped successfully.', 'Success', 'success');
            hideLoader();
          },
          (err) => {
            showToast('bda-stop-error', 'Could not stop application.', 'Application Error', 'danger');
            hideLoader();
          }
        );
    } catch (e) {
      console.log(e);
      showToast('bda-stop-error', 'Could not stop application.', 'Application Error', 'danger');
      hideLoader();
    }
  }

  const handleFilterChange = (filter: 'search' | 'mode' | 'accessLevel' | 'status') => (evt: any) => {
    switch (filter) {
      case 'search':
      default:
        setSearchText(evt.target.value);
        break;

      case 'mode':
        setModeFilter(evt);
        break;

      case 'accessLevel':
        setAccessLevelFilter(evt);
        break;

      case 'status':
        setStatusFilter(evt);
        break;
    }
  }

  const debounceSearchChange = useMemo(() => debounce(handleFilterChange("search"), 500), []);

  const handleItemsPerPageChange = (page: number, size: number) => {
    changePage(page);
    setItemsPerPage(size);
  }
  const changePage = (page: number) => setCurrentPage(page);

  // cleanup debounced handler
  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    }
  }, [debounceSearchChange]);

  const renderApplications = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return applications!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No applications found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            applications!.map(app => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={app.id}>
                <BdaApplicationItem
                  application={app}
                  onClick={navigateToDetail(app)}
                  onApplicationToggle={toggleApplication(app)}
                  onApplicationExport={downloadApplication(app)}
                  onApplicationEdit={navigateToEdit(app)}
                  onApplicationDelete={openDeleteModal(app)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  const renderAsyncContent = () => {
    return applications
      ? renderApplications()
      : (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      );
  }

  const renderToolbar = () => {
    return (
      <>
        <Row className="mb-2 align-items-center">
          <Col>
            <h4 className="mb-0">Applications List</h4>
          </Col>

          <Col xs={6} md={4} className="text-end">
            <Button size="sm" variant="success" onClick={navigateToCreation}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="ms-2">Create Application</span>
            </Button>
            <Button size="sm" variant="primary" className="ms-2" onClick={openImportModal}>
              <FontAwesomeIcon icon={solid('upload')} />
              <span className="ms-2">Upload Application</span>
            </Button>
          </Col>
        </Row>
        <Row>
          <Col md={3} className="mb-2">
            <AppSearch placeholder="Search application..." onChange={debounceSearchChange} />
          </Col>
          <Col md={2} className="mb-2">
            <Select
              placeholder="Select mode"
              value={modeFilter}
              allowClear
              onChange={handleFilterChange('mode')}
              className="w-100"
            >
              {modesOptions.map((opt, index) => (
                <Select.Option key={index} value={opt.value} label={opt.label}>
                  <div>
                    <FontAwesomeIcon icon={opt.icon} fixedWidth />
                    <span className="ms-2">{opt.label}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col md={2} className="mb-2">
            <Select
              mode="multiple"
              allowClear
              placeholder="Select Status"
              onChange={handleFilterChange('status')}
              options={statusOptions}
              value={statusFilter}
              className="w-100"
            />
          </Col>
          <Col md={2} className="mb-2">
            <Select
              placeholder="Select privacy level"
              value={accessLevelFilter}
              allowClear
              onChange={handleFilterChange('accessLevel')}
              className="w-100"
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col md={2} className="mb-2">

          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox checked={myApplications} onChange={handleMyApplicationsChange}>My Applications</Checkbox>
            <Checkbox className="ms-3" checked={defaultOnly} onChange={handleDefaultOnlyChange}>Default Only</Checkbox>
            <Checkbox className="ms-3" checked={activeSchedules} onChange={handleSchedulesChange}>Active Schedules</Checkbox>
          </Col>
          <Col className="text-center">
            <Pagination
              disabled={!applications}
              total={totalApplications}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              pageSize={itemsPerPage}
              current={currentPage}
              onChange={changePage}
              onShowSizeChange={handleItemsPerPageChange}
            />
          </Col>
          <Col md={2}>
            <Button size="sm" variant="light" className="w-100 border" onClick={changeSortMode}>
              <FontAwesomeIcon icon={sortDesc ? solid('sort-numeric-down-alt') : solid('sort-numeric-down')} />
              <span className="ms-2">Sort: <b>{sortDesc ? "descending" : "ascending"}</b></span>
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Container className="mt-3">
      {renderToolbar()}

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete application <b>{applicationToDelete && applicationToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteModalClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteApplication}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={handleImportModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Import Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="applicationFile" className="mb-3">
            <Form.Label>Choose Application file</Form.Label>
            <Form.Control type="file" size="sm" accept=".json" onChange={handleApplicationFile} />
          </Form.Group>

          {applicationImportError && <Form.Control.Feedback className="d-block" type="invalid">{applicationImportError}</Form.Control.Feedback>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="border" onClick={handleImportModalClose}>Close</Button>
          <Button variant="success" onClick={importApplication}>Import Application</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
